import React from 'react';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '../../store';
import { useLocation } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { Plug } from '@plone/volto/components/manage/Pluggable';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import flashSVG from '@plone/volto/icons/flash.svg';
import {
  setAssistantFloatingOpen,
  setAssistantPendingFocus,
} from '../../actions/assistant';
import AssistantPanel from './AssistantPanel';

const messages = defineMessages({
  assistant: {
    id: 'Assistant',
    defaultMessage: 'Assistant',
  },
});

const isEditRoute = (pathname) =>
  pathname.endsWith('/edit') || pathname.endsWith('/add');

/**
 * Global assistant chrome, mounted via config.settings.appExtras.
 *
 * Provides the single entry point — a button in the left toolbar
 * (main.toolbar.bottom plug). In view mode it toggles the floating panel;
 * in edit mode it focuses the Assistant tab in the sidebar (via the
 * pendingFocus flag the shadowed Sidebar reacts to).
 */
const AssistantExtras = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const intl = useIntl();

  const token = useAppSelector((state) => state.userSession?.token);
  const floatingOpen = useAppSelector((state) => state.assistant?.floatingOpen);

  const inEdit = isEditRoute(location.pathname);

  if (!token) return null;

  const onToggle = () => {
    if (inEdit) {
      dispatch(setAssistantPendingFocus(true));
    } else {
      dispatch(setAssistantFloatingOpen(!floatingOpen));
    }
  };

  return (
    <>
      <Plug
        pluggable="main.toolbar.bottom"
        id="ai-assistant"
        dependencies={[floatingOpen, inEdit]}
      >
        {() => (
          <button
            type="button"
            className={`assistant-toolbar-button${
              floatingOpen && !inEdit ? ' active' : ''
            }`}
            aria-label={intl.formatMessage(messages.assistant)}
            title={intl.formatMessage(messages.assistant)}
            onClick={onToggle}
          >
            <Icon name={flashSVG} size="30px" />
          </button>
        )}
      </Plug>
      {!inEdit && floatingOpen && <AssistantPanel host="floating" />}
    </>
  );
};

export default AssistantExtras;
